import { ClockFading, Filter } from 'lucide-react';
import { useState } from 'react';
import Search from '~/partials/Search';
import TabSwitch from '~/partials/TabSwitch';
import ListFiltersModal from './-ListFilters';
import { getRouteApi } from '@tanstack/react-router';
import { useList } from '~/util/hooks/useList';
import { rangeProperties } from './-SearchParams';
import { formatLevelGDID } from '~/util';
import ListTimeMachineModal from './-ListTimeMachine';
import { levelsQuery } from '~/util/queries/public/levels';
import { useHydratedQuery } from '~/util/hooks/useHydratedQuery';
import LevelsListData from './-ListData';

const Route = getRouteApi('/list');

function LevelsList({ onLevelSelect }: { onLevelSelect?: () => void }) {
    const list = useList();
    const { search, filters, sort, reverse, time_machine, tiers_mode } =
        Route.useSearch({
            select: ({
                search,
                filters,
                sort,
                reverse,
                time_machine,
                tiers_mode,
            }) => ({
                search,
                filters,
                sort,
                reverse,
                time_machine,
                tiers_mode,
            }),
        });

    const { data: other_data = [] } = useHydratedQuery(
        levelsQuery({
            list: list === 'classic' ? 'platformer' : 'classic',
            at: time_machine,
        }),
    );

    const navigate = Route.useNavigate();

    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isTimeMachineModalOpen, setIsTimeMachineModalOpen] = useState(false);

    const hasActiveSearchSettings =
        sort != 'rank' ||
        reverse != false ||
        tiers_mode != false ||
        Object.keys(filters.tags).length > 0 ||
        filters.tiers.length > 0 ||
        rangeProperties.some((property) => {
            const range = filters.ranges[property];
            return range.min !== undefined || range.max !== undefined;
        }) ||
        filters.completed_by_user !== 'all';

    return (
        <>
            <div className="flex flex-col items-center gap-4 w-full">
                <TabSwitch
                    options={['classic', 'platformer']}
                    current={list}
                    onChange={(list) => {
                        navigate({
                            to: '/list/$levelid',
                            params: {
                                levelid:
                                    other_data && other_data.length > 0
                                        ? formatLevelGDID(other_data[0])
                                        : '',
                            },
                            search: {
                                list,
                                records: {},
                            },
                        });
                    }}
                    className="pt-4 pb-2 w-5/6"
                    size="md"
                />
                <div className="flex justify-between items-center mb-2 w-11/12">
                    <Search
                        initial_input={search}
                        onInputChange={(value: string) => {
                            navigate({
                                from: '/list/$levelid',
                                search: (prev) => ({
                                    ...prev,
                                    search: value,
                                }),
                                replace: true,
                            });
                        }}
                    />
                    <button
                        type="button"
                        className="relative mx-2 transition-colors text-secondary-light hover:text-secondary-foreground cursor-pointer"
                        onClick={() => setIsFiltersModalOpen(true)}
                        aria-label="Open list filters"
                    >
                        <Filter size={32} />
                        {hasActiveSearchSettings && (
                            <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-primary" />
                        )}
                    </button>
                    <button
                        type="button"
                        className="relative mx-2 transition-colors text-secondary-light hover:text-secondary-foreground cursor-pointer"
                        onClick={() => setIsTimeMachineModalOpen(true)}
                        aria-label="Open time machine"
                    >
                        <ClockFading size={32} />
                        {time_machine != undefined && (
                            <span className="absolute -right-1 -top-0.5 size-2.5 rounded-full bg-primary" />
                        )}
                    </button>
                </div>

                <LevelsListData onLevelSelect={onLevelSelect} />
            </div>
            <ListFiltersModal
                isOpen={isFiltersModalOpen}
                onClose={() => setIsFiltersModalOpen(false)}
            />
            <ListTimeMachineModal
                open={isTimeMachineModalOpen}
                onOpenChange={setIsTimeMachineModalOpen}
            />
        </>
    );
}

export default LevelsList;
